import Decimal from "decimal.js";
import { z } from "zod";

// Cede Arena external (non-USD) rewards: a partner-supplied off-platform prize
// replaces the USDT payout. Participants still pay a buy-in; the pot is Cede
// revenue and every winning rank is credited 0.
//
// `prizePool` still holds the collected buy-ins — do NOT zero it. The winner is
// found via a query gated on `LeaderboardRecord.rewardTokenAmount > 0` (also the
// settlement signal), so a zero pool leaves the real winner reading "Didn't win".
//
// Own file, not the cede-arena index, so campaign DTOs import it without a cycle
// through the barrel — same reason as `rake.ts`.

/** How a Cede Arena competition's prize reaches its winner. */
export const ARENA_REWARD_KIND = {
  /** Default — the prize pool is paid out in USDT to the winning ranks. */
  CASH: "CASH",
  /** A partner-supplied off-platform prize; the collected buy-ins are house revenue. */
  EXTERNAL: "EXTERNAL",
} as const;
export type ArenaRewardKind = (typeof ARENA_REWARD_KIND)[keyof typeof ARENA_REWARD_KIND];
export const ArenaRewardKindSchema = z.enum(ARENA_REWARD_KIND);

/**
 * Label length cap. The social card renders the prize into a fixed-width chip
 * (where a cash pool reads "5K USDT"), so an unbounded label would overflow the
 * OG image rather than merely wrap in the UI.
 */
export const ARENA_EXTERNAL_REWARD_LABEL_MAX_LENGTH = 60;

/** Max labelled ranks. Well past any real competition; bounds the JSON column. */
export const ARENA_EXTERNAL_REWARD_SLOT_LABELS_MAX = 50;

export const ArenaExternalRewardLabelSchema = z
  .string()
  .trim()
  .min(1, { error: "The reward label cannot be empty" })
  .max(ARENA_EXTERNAL_REWARD_LABEL_MAX_LENGTH, {
    error: `The reward label must be at most ${ARENA_EXTERNAL_REWARD_LABEL_MAX_LENGTH} characters`,
  });

/**
 * Per-rank labels, positional: index `i` labels rank `i + 1`; an empty entry means
 * that rank wins nothing. Repeat a label to share one prize across a rank range.
 *
 * Positional, not alongside the PnL rule's `slots`: those are rewritten wholesale
 * at competition start (rescaled, re-sorted, capped), which would destroy any
 * label carried with them.
 *
 * Omits the rank-1-filled rule that every write path applies
 * ({@link assertArenaExternalRewardInvariants}) — this schema also guards
 * hydration, where a stricter rule turns saved rows into read errors.
 */
export const ArenaExternalRewardSlotLabelsSchema = z
  .array(z.string().trim().max(ARENA_EXTERNAL_REWARD_LABEL_MAX_LENGTH))
  .min(1, { error: "Provide at least one slot label, or leave slot labels empty" })
  .max(ARENA_EXTERNAL_REWARD_SLOT_LABELS_MAX)
  .refine((labels) => labels.some((label) => label.length > 0), {
    error: "At least one slot label must be non-empty",
  });

/**
 * Read the slot labels off a persisted JSON column. Anything that isn't an array of
 * strings is malformed data and reads as absent rather than being coerced — a
 * half-parsed label array would silently mis-assign prizes to ranks.
 */
export const parseArenaExternalRewardSlotLabels = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;
  return value.every((entry) => typeof entry === "string") ? (value as string[]) : null;
};

/** The label shown for `rank`, or `undefined` when that rank has no prize text. */
export const resolveArenaExternalRewardSlotLabel = (
  slotLabels: string[] | null | undefined,
  rank: number,
): string | undefined => {
  const label = slotLabels?.[rank - 1]?.trim();
  return label ? label : undefined;
};

/**
 * Write-path invariants. Every cash knob must be off — each is a lever on a payout
 * that no longer happens — and the opt-in buy-in must be positive, since it is both
 * the house's revenue and what makes the competition rank as staked.
 *
 * Scheduled competitions only; 1v1 duels are cash-only. Re-enforced by the
 * `TradeToEarnCampaign` entity.
 */
export const assertArenaExternalRewardInvariants = (
  data: {
    arenaRewardKind?: ArenaRewardKind | null;
    arenaExternalRewardLabel?: string | null;
    arenaRakeBps?: number | null;
    arenaCreatorRakeBps?: number | null;
    isSpinAndGo?: boolean | null;
    cedeFillPrizePool?: boolean | null;
    entryFees?: string | null;
    userCanOptIn?: boolean | null;
    arenaExternalRewardSlotLabels?: string[] | null;
  },
  ctx: z.RefinementCtx,
): void => {
  if (data.arenaRewardKind !== ARENA_REWARD_KIND.EXTERNAL) {
    if (data.arenaExternalRewardLabel != null && data.arenaExternalRewardLabel.trim().length > 0) {
      ctx.addIssue({
        code: "custom",
        path: ["arenaExternalRewardLabel"],
        message: 'A reward label is only allowed when the reward kind is "EXTERNAL"',
      });
    }
    if (data.arenaExternalRewardSlotLabels != null) {
      ctx.addIssue({
        code: "custom",
        path: ["arenaExternalRewardSlotLabels"],
        message: 'Slot labels are only allowed when the reward kind is "EXTERNAL"',
      });
    }
    return;
  }

  if (!(data.arenaExternalRewardLabel != null && data.arenaExternalRewardLabel.trim().length > 0)) {
    ctx.addIssue({
      code: "custom",
      path: ["arenaExternalRewardLabel"],
      message: "An external reward requires a label describing the prize",
    });
  }
  // The buy-ins ARE the house's revenue: a Cede-funded competition would book
  // revenue nobody paid, and would fail the staked-competition predicate.
  const isPositive = (value: string | null | undefined): boolean => {
    if (value == null || value === "") return false;
    try {
      return new Decimal(value).greaterThan(0);
    } catch {
      return false;
    }
  };
  if (!(data.userCanOptIn && isPositive(data.entryFees))) {
    ctx.addIssue({
      code: "custom",
      path: ["entryFees"],
      message: "An external-reward competition requires a positive opt-in entry fee",
    });
  }
  // Slot labels outrank the headline everywhere a per-rank prize shows, so a blank
  // 1st entry blanks the champion's cells while the card still advertises the prize.
  const firstSlotLabel = data.arenaExternalRewardSlotLabels?.[0];
  if (data.arenaExternalRewardSlotLabels != null && (firstSlotLabel == null || firstSlotLabel.trim() === "")) {
    ctx.addIssue({
      code: "custom",
      path: ["arenaExternalRewardSlotLabels"],
      message: "The 1st-place slot label must be filled — it replaces the reward label for rank 1",
    });
  }
  if (data.arenaRakeBps) {
    ctx.addIssue({
      code: "custom",
      path: ["arenaRakeBps"],
      message: "An external-reward competition pays no cash, so its rake must be 0 (the house keeps the whole pot)",
    });
  }
  if (data.arenaCreatorRakeBps) {
    ctx.addIssue({
      code: "custom",
      path: ["arenaCreatorRakeBps"],
      message: "An external-reward competition pays no cash, so its creator rake must be 0",
    });
  }
  if (data.isSpinAndGo) {
    ctx.addIssue({
      code: "custom",
      path: ["isSpinAndGo"],
      message: "Spin & Go resizes a cash pool, so it cannot be combined with an external reward",
    });
  }
  if (data.cedeFillPrizePool) {
    ctx.addIssue({
      code: "custom",
      path: ["cedeFillPrizePool"],
      message: "Cede cannot guarantee a cash pool on an external-reward competition",
    });
  }
};
